import { memo } from 'react';
import { mapValues, omit } from 'lodash';

import { StudioVisualizationType } from '@blockworks/platform/api/research/studio';
import { usePrevious } from '@blockworks/platform/client';
import { FlexBox, Spinner, Text } from '@blockworks/ui';

import { useVisualizationOptionsContext } from '../../../../context';
import { ExportExecutionResults } from '../../../export-execution-results';
import { VisualizationProvider } from '../../context';
import { URLBOX_ELEMENT_SELECTOR_ID, VisualizationError, VisualizationErrorType } from '../../models';
import { useWorkerVisualizationProps } from '../../worker/visualization-worker.queries';
import { ExpandVisualization } from '../expand-visualization';
import { ParameterControls } from '../parameter-controls';
import {
    PARAMETER_CONTROLS_CONTAINER_HEIGHT,
    PARAMETER_CONTROLS_CONTAINER_HTML_ID,
} from '../parameter-controls/parameter-controls.model';
import { DownloadUnbrandedVisualizationScreenshot, ShareVisualization } from '../share-visualization';
import { VisualizationErrorMessage } from '../visualization-error-message/visualization-error-message';
import { VisualizationHeader } from '../visualization-header';
import { VISUALIZATION_HEADER_HTML_ID } from '../visualization-header/visualization-header.model';

import {
    getVisualizationComponent,
    QuickActions,
    VisualizationInnerWrapper,
    VisualizationWrapper,
} from './visualization-renderer.components';
import { FEATURE_CONTROL_BUTTONS_BAR_ID, type VisualizationRendererProps } from './visualization-renderer.model';

const VisualizationRenderer = memo(function VisualizationRenderer({
    visualization,
    editing,
    componentSlots,
    disableExpand = false,
    disableSharing = false,
    disableExport = false,
}: VisualizationRendererProps) {
    const { features, layout } = useVisualizationOptionsContext();
    const { config, title, execution, type, id, description, subTitle, slug } = visualization;
    const {
        isEnabled: parameterControlsEnabled,
        activeControls,
        setParams,
        params,
    } = ParameterControls.useActiveControlsState(visualization);
    const propsQuery = useWorkerVisualizationProps({
        variables: {
            config,
            execution: execution,
            title,
            description,
            params,
            type,
            editing,
            id,
            // Can do better than this
            activeControls: mapValues(activeControls, val => omit(val, 'views')),
        },
        enabled: !!config,
        retry: (failureCount, error) => {
            if (error instanceof VisualizationError) {
                if (error.type !== VisualizationErrorType.Worker) {
                    return false;
                }
            }

            return failureCount < 3;
        },
    });
    // If we have rendered this previously we should keep old props around
    // until we have our new props to avoid unnecessary loading states
    // this improves parameter transitions
    const recentProps = usePrevious(propsQuery.data);
    const visualizationProps = propsQuery.data || recentProps;
    const exportEnabled =
        !disableExport &&
        ((config.features?.export !== false && type === StudioVisualizationType.Timeseries) || config.features?.export);

    const hasParameterControls =
        Object.values(activeControls).filter(parameter => {
            if (parameter.hidden || parameter.views.length <= 1) return false;
            return true;
        }).length > 0 && parameterControlsEnabled;

    const featureControls = (
        <>
            {features.quickActions && (
                <QuickActions quickActions={features.quickActions} visualization={visualization} />
            )}
            {features.dangerouslyAllowUnbrandedScreenshotDownloads && (
                <DownloadUnbrandedVisualizationScreenshot visualization={visualization} />
            )}
            {!disableSharing && (
                <ShareVisualization visualization={visualization} socialMediaSharing={features.socialMediaSharing} />
            )}
            {exportEnabled && <ExportExecutionResults visualization={visualization} />}
            {!disableExpand && (
                <ExpandVisualization visualization={visualization}>
                    <VisualizationRenderer
                        {...{ visualization, editing }}
                        disableExpand
                        disableSharing={disableSharing}
                        disableExport={disableExport}
                    />
                </ExpandVisualization>
            )}
        </>
    );

    const { HeaderEnd, HeaderTitle, Header } = componentSlots ?? {};
    const VisualizationComponent = getVisualizationComponent(type);
    const headerEnd = HeaderEnd ? <HeaderEnd /> : undefined;
    const featureCount = [
        ...(features?.quickActions?.map(f => !!f) ?? []),
        !disableSharing,
        !disableExpand,
        exportEnabled,
        headerEnd,
    ].filter(Boolean).length;
    const parameterControls = hasParameterControls && (
        <FlexBox
            gap="xs"
            className="scroller scroller-xs"
            overflowX="auto"
            justifyContent="between"
            borderBottom={1}
            px={2}
            py={1}
            style={{ height: PARAMETER_CONTROLS_CONTAINER_HEIGHT }}
            id={PARAMETER_CONTROLS_CONTAINER_HTML_ID}
        >
            <FlexBox gap="xs" alignItems="center">
                <ParameterControls
                    visualization={visualization}
                    setParams={setParams}
                    activeControls={activeControls}
                    params={params}
                />
            </FlexBox>
        </FlexBox>
    );

    // Show loading state if we either don't have data or do but bave an error we're recovering from
    const isLoading = (!visualizationProps && propsQuery.isLoading) || (propsQuery.error && propsQuery.isLoading);
    const isError = propsQuery.isError;

    const renderVisualizationContent = () => {
        if (isLoading) {
            return (
                <FlexBox alignItems="center" justifyContent="center" gap={2} h="full" w="full">
                    <Spinner size={10} color="purple" />
                </FlexBox>
            );
        }
        if (isError) {
            if (features.verboseErrorMessages && propsQuery.error instanceof VisualizationError) {
                return <VisualizationErrorMessage error={propsQuery.error} />;
            }
            return (
                <FlexBox alignItems="center" justifyContent="center" gap={2} h="full" w="full">
                    <Text size="sm">Something went wrong, please try again later...</Text>
                </FlexBox>
            );
        }

        return (
            <FlexBox w="full" h="full" id={URLBOX_ELEMENT_SELECTOR_ID}>
                {!!VisualizationComponent && <VisualizationComponent {...visualizationProps} />}
            </FlexBox>
        );
    };

    return (
        <VisualizationProvider>
            <VisualizationWrapper>
                {Header ? (
                    <FlexBox col>
                        <Header
                            queryId={visualization.queryId}
                            visualizationId={visualization.id}
                            title={title}
                            slug={slug}
                        />
                        {parameterControls}
                    </FlexBox>
                ) : (
                    <FlexBox col>
                        <FlexBox
                            gap="xs"
                            alignItems="center"
                            borderBottom={1}
                            px={3}
                            pr={2}
                            maxW="full"
                            style={
                                {
                                    height: layout.headerHeight,
                                    '--feature-bar-size': `${featureCount * 24}px`,
                                } as React.CSSProperties
                            }
                            id={VISUALIZATION_HEADER_HTML_ID}
                        >
                            <FlexBox className="max-w-[calc(100%-var(--feature-bar-size))]" flexGrow={1}>
                                <VisualizationHeader
                                    title={{ label: title, Component: HeaderTitle }}
                                    visualizationId={id}
                                    description={description}
                                    queryId={visualization.queryId}
                                    subTitle={subTitle}
                                    slug={slug}
                                />
                            </FlexBox>
                            <FlexBox
                                justifyContent="end"
                                gap="xs"
                                minW="max"
                                className="min-w-[--feature-bar-size]"
                                id={FEATURE_CONTROL_BUTTONS_BAR_ID}
                            >
                                {featureControls}
                                {headerEnd}
                            </FlexBox>
                        </FlexBox>
                        {parameterControls}
                    </FlexBox>
                )}
                <VisualizationInnerWrapper type={type} hideWordmark={isError}>
                    {renderVisualizationContent()}
                </VisualizationInnerWrapper>
            </VisualizationWrapper>
        </VisualizationProvider>
    );
});

export { VisualizationRenderer };
